import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Input, Layout, Tooltip } from 'antd';
import {
  FullscreenExitOutlined,
  FullscreenOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';

import CampusModal from '../Modal/CampusModal';
import FullScreenButton from 'src/components/common/FullScreenButton';
import OpenAiModal from '../Modal/OpenAiModal';
import StudentModal from '../Modal/StudentModal';
import TeacherModal from 'src/components/Modal/TeacherModal';
import { userInfoVar, userVar } from 'src/apollo/cache';
import { GET_STUDENT_LUPUSER, GET_TEACHER_USER } from 'src/operations/queries/getUser';
import { USER_ME } from 'src/operations/queries/getUser';
import { useQueryProxy } from 'src/operations/proxy/user';
import { getCurrentToken } from '../../utils';
import { useModal } from 'src/utils/hooks';
import * as queries from 'src/operations/queries';

const { Header: HeaderWrapper } = Layout;

const HeaderLevelUp = styled(HeaderWrapper)`
  position: ${({ isStatic }) => (isStatic ? 'fixed' : 'sticky')};
  top: 0;
  z-index: 100;
  width: 100%;
  height: auto;
  min-height: 64px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #ffffff;
  border-bottom: 1.5px solid rgba(8, 145, 178, 0.14);
  box-shadow: 0 2px 16px rgba(8, 145, 178, 0.1);
  line-height: 1;

  @media (max-width: 1024px) {
    padding: 10px 16px;
    gap: 10px;
  }
`;

const LeftSection = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
`;

const RightSection = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const RoundIconButton = styled.button`
  width: 38px;
  height: 38px;
  border: 1.5px solid rgba(8, 145, 178, 0.16);
  border-radius: 14px;
  background: ${(props) => props.background || '#ffffff'};
  color: ${(props) => props.color || '#4a6380'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: #0891b2;
    color: #0891b2;
    background: rgba(8, 145, 178, 0.08);
  }
`;

const LogoGroup = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
`;

const LogoIcon = styled.div`
  font-size: 28px;
  line-height: 1;
`;

const LogoText = styled.div`
  font-size: 20px;
  font-weight: 900;
  color: #0891b2;
  letter-spacing: -0.01em;

  span {
    color: #f97316;
  }
`;

const NavGroup = styled.nav`
  display: flex;
  align-items: center;
  gap: 2px;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const NavItem = styled.button`
  padding: 6px 14px;
  border: 0;
  border-radius: 8px;
  background: ${(props) => (props.$active ? 'rgba(8, 145, 178, 0.1)' : 'transparent')};
  color: ${(props) => (props.$active ? '#0891b2' : '#4a6380')};
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? 800 : 700)};
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: rgba(8, 145, 178, 0.1);
    color: #0891b2;
  }
`;

const NavEmoji = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 6px;
`;

const SearchWrap = styled.div`
  width: min(320px, 100%);

  .ant-input-affix-wrapper {
    border-radius: 999px;
    border: 1.5px solid rgba(8, 145, 178, 0.14);
    background: #e8f4f8;
    padding: 7px 14px;
    box-shadow: none;
  }

  .ant-input-affix-wrapper-focused,
  .ant-input-affix-wrapper:focus-within {
    border-color: #0891b2;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.1);
  }

  .ant-input {
    font-size: 13px;
    font-weight: 600;
    background: transparent;
  }

  @media (max-width: 900px) {
    width: 180px;
  }

  @media (max-width: 720px) {
    display: none;
  }
`;

const SearchActionIcon = styled(SearchOutlined)`
  color: #8fa3bb;
  cursor: pointer;
  transition: color 0.18s ease;

  &:hover {
    color: #0891b2;
  }
`;

const UserCard = styled.button`
  padding: 5px 14px 5px 8px;
  border: 1.5px solid rgba(8, 145, 178, 0.14);
  border-radius: 999px;
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    border-color: #0891b2;
    box-shadow: 0 2px 12px rgba(8, 145, 178, 0.12);
  }
`;

const UserMeta = styled.div`
  text-align: left;
  line-height: 1.2;
`;

const OwlAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid rgba(8, 145, 178, 0.2);
  background: linear-gradient(135deg, #0891b2, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
`;

const UserName = styled.div`
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0c1a2e;
  font-size: 12px;
  font-weight: 800;
`;

const UserSub = styled.div`
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #8fa3bb;
  font-size: 10px;
  font-weight: 700;
`;

const CloseEmojiButton = styled(RoundIconButton)`
  font-size: 18px;
  color: #ef4444;

  &:hover {
    border-color: #ef4444;
    color: #ef4444;
    background: rgba(239, 68, 68, 0.08);
  }
`;

const navItems = [
  { key: 'home', emoji: '\u{1F3E0}', label: 'LMS관리', path: '/level-up/booklist', match: (pathname) => pathname.startsWith('/level-up/booklist') },
  { key: 'home', emoji: '\u{1F3E0}', label: '홈', path: '/level-up', match: (pathname) => pathname === '/level-up' },
  { key: 'library', emoji: '\u{1F4DA}', label: '도서관', path: '/level-up/library', match: (pathname) => pathname.startsWith('/level-up/library')  },
  // { key: 'library', emoji: '\u{1F4DA}', label: '도서관', path: '/level-up', match: (pathname) => pathname==='/level-up'  },
  // { key: 'portfolio', emoji: '\u{1F4CA}', label: '학습현황', path: '/level-up/portfolio', match: (pathname) =>['portfolio','favorite','inprogress','calendar'].some((v) => pathname.includes(v)) },
  // { key: 'results', label: '랭킹', path: '/level-up/results/readers', match: (pathname) => pathname.startsWith('/level-up/results') },
];
const navItemsForStudents = [
  { key: 'home', emoji: '\u{1F3E0}', label: '홈', path: '/level-up', match: (pathname) => pathname === '/level-up' },
  { key: 'library', emoji: '\u{1F4DA}', label: '도서관', path: '/level-up/library', match: (pathname) => pathname.startsWith('/level-up/library')  },
  { key: 'portfolio', emoji: '\u{1F4CA}', label: '학습현황', path: '/level-up/portfolio', match:  (pathname) =>['portfolio','favorite','inprogress','calendar'].some((v) => pathname.includes(v)) },
  // { key: 'results', label: '랭킹', path: '/level-up/results/readers', match: (pathname) => pathname.startsWith('/level-up/results') },
];

const Header = ({ collapsed, onCollapse, isStatic = false, hideMenu = false, closeHeader = false }) => {
  const history = useHistory();
  const location = useLocation();
  const token = getCurrentToken();
  const [searchValue, setSearchValue] = useState('');
  const [openAiModal, setOpenAiModal] = useState(false);
  const { visible, showModal, handleCancel } = useModal();
  const { data } = useQueryProxy(USER_ME, { variables: { token }, skip: !token });
  const { data: userInfo } = useQuery(queries.getUser.GET_USER, { skip: !token });

  const userType = useMemo(() => {
    if (userInfoVar()) {
      return userVar()[userInfoVar().type];
    }
    return '';
  }, []);

  useEffect(() => {
    const keyword = new URLSearchParams(location.search).get('keyword');
    setSearchValue(keyword || '');
  }, [location.pathname, location.search]);

  const levelupUserQuery = userInfoVar() &&  parseInt(userInfoVar()?.type) !== 0 ? GET_TEACHER_USER : GET_STUDENT_LUPUSER;
  const { data: levelupUserData } = useQuery(levelupUserQuery);

  const onLogout = () => {
   history.push('/')
  };

  const onSubmitSearch = () => {
    const keyword = searchValue.trim();
    if (keyword.length < 2) {
      return;
    }
    history.push(`/level-up/search?keyword=${encodeURIComponent(keyword)}`);
  };

  const onClose = () => {
    if (window.Android?.closeApp) {
      window.Android.closeApp();
      return;
    }
    history.goBack();
  };

  const displayName = data?.me?.name || userInfo?.getUserData?.name || 'Level Up User';
  const displayGrade = levelupUserData?.getUserData?.school_grade || data?.me?.school_grade || userInfo?.getUserData?.school_grade || '';
  const showCloseButton = closeHeader || Boolean(window.Android?.closeApp);
  const classLevel = useMemo(() => {
    const userData = levelupUserData?.getUserData || userInfo?.getUserData;
    const classes =
      userData?.classes ||
      userData?.class_student?.map((item) => item?.class).filter(Boolean) ||
      userData?.class_teacher?.map((item) => item?.class).filter(Boolean) ||
      [];

    const latestTest = [...(userData?.user_levelup_tests || [])]
      .filter((item) => item?.level)
      .sort((a, b) => new Date(b?.test_end_date || 0).getTime() - new Date(a?.test_end_date || 0).getTime())[0];

    if (latestTest?.lexile && latestTest?.level) {
      return latestTest.level;
    }

    const latestClass = [...classes]
      .filter((item) => item?.levelup_level !== undefined && item?.levelup_level !== null && item?.levelup_level !== '')
      .sort((a, b) => new Date(b?.begin_date || 0).getTime() - new Date(a?.begin_date || 0).getTime())[0];

    return latestClass?.levelup_level ?? 1;
  }, [levelupUserData, userInfo]);

  const handleNavClick = (item) => {
    if (item.key === 'library') {
      const libraryLevel = classLevel === 'PH' ? 0 : classLevel;
      history.push(`/level-up/library?lv=${libraryLevel}`);
      return;
    }
    history.push(item.path);
  };

  return (
    <>
      <HeaderLevelUp isStatic={isStatic}>
        <LeftSection>
          {/* {!hideMenu && (
            <RoundIconButton type="button" onClick={onCollapse} aria-label="toggle navigation">
              {collapsed ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            </RoundIconButton>
          )} */}

          <LogoGroup type="button" onClick={() => history.push('/level-up')}>
            <LogoIcon><img src="/images/level_up_logo.svg" style={{ height: 40 }} /></LogoIcon>
            <LogoText>
              e-<span>Library</span>
            </LogoText>
          </LogoGroup>

          <NavGroup>
            {(parseInt(userInfoVar()?.type) !== 0 ? navItems:navItemsForStudents).map((item) => (
              <NavItem key={item.key} type="button" $active={item.match(location.pathname)} onClick={() => handleNavClick(item)}>
                <NavEmoji>{item.emoji}</NavEmoji>
                {item.label}
              </NavItem>
            ))}
          </NavGroup>
        </LeftSection>

        <RightSection>
          <SearchWrap>
            <Input
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              onPressEnter={onSubmitSearch}
              placeholder="Search books or keywords"
              suffix={<SearchActionIcon onClick={onSubmitSearch} />}
            />
          </SearchWrap>
{/* 
          <Tooltip title="Notifications">
            <RoundIconButton type="button" aria-label="notifications">
              {'\u{1F514}'}
            </RoundIconButton>
          </Tooltip> */}

          <Tooltip overlayInnerStyle={{ color: '#ffffff' }} color='#06b6d4' title="학습안내">
            <RoundIconButton onClick={()=>{window.open('https://cdn.cloubot.com/LEVELUP/guide/index.html','_blank')}} type="button" aria-label="help">
              {'\u2753'}
            </RoundIconButton>
          </Tooltip>

          <Tooltip overlayInnerStyle={{ color: '#ffffff' }} color='#06b6d4' title="학습종료">
            <RoundIconButton type="button" onClick={onLogout} aria-label="logout">
              <LogoutOutlined />
            </RoundIconButton>
          </Tooltip>

          <FullScreenButton
            element={
              <Tooltip overlayInnerStyle={{ color: '#ffffff' }} color='#06b6d4' title="화면확대">
                <RoundIconButton type="button" aria-label="enter fullscreen">
                  <FullscreenOutlined />
                </RoundIconButton>
              </Tooltip>
            }
            activeElement={
              <Tooltip overlayInnerStyle={{ color: '#ffffff' }} color='#06b6d4' title="화면축소">
                <RoundIconButton type="button" aria-label="exit fullscreen">
                  <FullscreenExitOutlined />
                </RoundIconButton>
              </Tooltip>
            }
          />

          <UserCard type="button" 
          // onClick={() => showModal('edit')}
            >
            <OwlAvatar>{'\u{1F989}'}</OwlAvatar>
            <UserMeta>
              <UserName>{displayName}</UserName>
              <UserSub>{displayGrade || ''}</UserSub>
            </UserMeta>
          </UserCard>

          {showCloseButton && (
            <Tooltip title="Close">
              <CloseEmojiButton type="button" onClick={onClose} aria-label="close">
                {'\u274C'}
              </CloseEmojiButton>
            </Tooltip>
          )}
        </RightSection>
      </HeaderLevelUp>

      {userType === 'fc' && (
        <TeacherModal userInfo={userInfo?.getUserData} popupMode="edit" handleCancel={handleCancel} visible={visible} myInfo={true} />
      )}
      {userType === 'campus' && (
        <CampusModal
          campusInfo={userInfo?.getUserData?.campus}
          popupMode="edit"
          handleCancel={handleCancel}
          visible={visible}
          campus_idx={userInfo?.getUserData?.campus?.idx}
        />
      )}
      {userType === 'manager' && (
        <TeacherModal userInfo={userInfo?.getUserData} popupMode="edit" handleCancel={handleCancel} visible={visible} myInfo={true} />
      )}
      {userType === 'student' && (
        <StudentModal userInfo={userInfo?.getUserData} popupMode="edit" handleCancel={handleCancel} visible={visible} myInfo={true} />
      )}
      <OpenAiModal
        visible={openAiModal}
        handleCancel={() => {
          setOpenAiModal(false);
        }}
      />
    </>
  );
};

export default Header;
